/**
 * The Users screen (f3.1) -- Platform Admin only, reads included.
 *
 * This is the screen f1.3 pointed at when it recorded that *"role grants
 * require a direct database update until f3.1 delivers the Users screen"*.
 *
 * **What the prototype draws that is deliberately absent**, so a reviewer can
 * check the list rather than file six defects (design D6):
 *
 * | Prototype element | Why it is not here |
 * | --- | --- |
 * | `Invite user` | Renamed, not removed: the button is `Add user`. Nothing is sent to anybody (ADR-5 removed the invitation e-mail), so calling it an invite promised a message that never arrives. The account is created and waits for that person's first Entra sign-in to claim it |
 * | `Resend invite` | Never built, in any feature -- there is no invitation to resend. Cancelling one is deactivating the account |
 * | `Link to person` | Never built, and never will be: c-strict reversed ADR-19 and deleted `Person` outright, so this account IS the human and there is nothing left to link it to |
 * | `Scope` column and the group `select` | Never built: f1.3 D2 dropped the one scoped role, so a platform role is always global |
 * | `Delivery roles held` | Phase 9, as a read-only projection of this account's own assignments -- never an editable control here, because a delivery role is named on the node it is held at |
 * | Two role badges on one user | Never built: f1.3 D1 ruled one role per user |
 * | `Export` | Dropped. No feature asks for it, and f1.5 refused an audit export on the same ground |
 *
 * Visibility comes from the server's navigation list, and hiding the screen is
 * presentation only: every call below is refused server-side for anyone who is
 * not a Platform Admin, and the refusal is recorded.
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  RotateCcw,
  RotateCw,
  Search,
  Shield,
  ShieldAlert,
  UserCog,
  UserPlus,
  UserX,
  Users as UsersIcon,
} from 'lucide-react';

import { ApiError, type UserAccount } from '@/api/client';
import { useLookupCatalog, useUpdateUser, useUsers } from '@/api/queries';
import { useSessionScope } from '@/session/SessionScopeContext';
import { SortableHeader } from '@/ui/SortableHeader';
import { nextSort, type SortState } from '@/ui/tableSort';

import { InviteUserDialog } from '@/users/InviteUserDialog';

import { DeactivateDialog, RoleDialog } from './RoleDialog';
import {
  DEFAULT_SORT,
  PLATFORM_ROLE_TYPE,
  applyFilters,
  badgeClassFor,
  formatSignIn,
  initialsOf,
  orderedForDisplay,
  statusOptions,
  statusPresentation,
  type SortColumn,
} from './userView';

/** The navigation key f3.1 narrowed to Platform Admin. */
const NAV_KEY = 'users';

const OWN_ROW_REASON =
  'You cannot change your own role or deactivate your own account. Another Platform Admin must make this change.';

/**
 * `onAdd` is optional, and its absence is meaningful rather than convenient.
 *
 * The refusal, loading and failure states render the same head so the screen
 * does not jump as it settles -- but none of them has a loaded role catalogue
 * to build the dialog's picker from, so none of them offers the action. A
 * button that can only fail is worse than one that is not there yet.
 */
function PageHead({ onAdd }: { onAdd?: () => void }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Users</h1>
        <div className="page-sub">Atlas accounts and the platform roles granted to them</div>
      </div>
      <div className="page-actions">
        <span className="badge pill-admin">
          <Shield aria-hidden="true" /> Admin only
        </span>
        {onAdd && (
          <button type="button" className="btn btn-primary" onClick={onAdd}>
            <UserPlus aria-hidden="true" /> Add user
          </button>
        )}
      </div>
    </div>
  );
}

function StateCard({
  icon,
  warning = false,
  title,
  children,
}: {
  icon: ReactNode;
  warning?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="empty">
          <div className={`e-icon${warning ? ' e-icon-warning' : ''}`}>{icon}</div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

function NoAccess() {
  return (
    <>
      <PageHead />
      <StateCard warning icon={<ShieldAlert aria-hidden="true" />} title="Platform Admins only">
        <p>
          The account roster and every role grant are restricted to Platform Admins. The server
          refuses anyone else, and records the refusal in the audit log.
        </p>
      </StateCard>
    </>
  );
}

/** Bars, not an empty table: distinguishable from a roster with no rows. */
function LoadingSkeleton() {
  return (
    <>
      <PageHead />
      <div className="card" aria-busy="true" aria-label="Loading accounts">
        <div className="card-body">
          {[0, 1, 2, 3, 4].map((index) => (
            <span key={index} className="skeleton-bar users-skeleton-line" />
          ))}
        </div>
      </div>
    </>
  );
}

export function UsersPage() {
  const { canSee } = useSessionScope();
  // Presentation only: the navigation list is the server's decision, and the
  // server refuses the API calls regardless of what renders here.
  if (!canSee(NAV_KEY)) return <NoAccess />;
  return <UsersScreen />;
}

export function UsersScreen() {
  const roster = useUsers();
  // The SAME hook the Reference data screen uses. No second fetch of the same
  // endpoint, and no roles endpoint: f2.2's catalogue is the one source.
  const catalog = useLookupCatalog();
  const { user: caller } = useSessionScope();

  const [search, setSearch] = useState('');
  const [roleCode, setRoleCode] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState<SortState<SortColumn>>(DEFAULT_SORT);
  const [editing, setEditing] = useState<UserAccount | null>(null);
  const [deactivating, setDeactivating] = useState<UserAccount | null>(null);
  const [adding, setAdding] = useState(false);

  const roleType = useMemo(
    () => catalog.data?.types.find((type) => type.code === PLATFORM_ROLE_TYPE),
    [catalog.data],
  );
  const roles = roleType?.values ?? [];

  const users = roster.data?.users ?? [];
  const statuses = useMemo(() => statusOptions(users), [users]);
  const visible = useMemo(
    () => orderedForDisplay(applyFilters(users, { search, roleCode, status }), sort),
    [users, search, roleCode, status, sort],
  );

  if (roster.isPending || catalog.isPending) return <LoadingSkeleton />;

  const failure = roster.error ?? catalog.error;
  if (failure) {
    // A 403 is a refusal, not a failure -- and it never triggers the
    // interactive re-authentication a 401 does (f1.2).
    if (failure instanceof ApiError && failure.status === 403) return <NoAccess />;
    return (
      <>
        <PageHead />
        <StateCard
          warning
          icon={<AlertTriangle aria-hidden="true" />}
          title="The accounts could not be loaded"
        >
          <p>{failure instanceof Error ? failure.message : 'Unexpected error.'}</p>
          <p className="empty-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                void roster.refetch();
                void catalog.refetch();
              }}
            >
              <RotateCw aria-hidden="true" /> Retry
            </button>
          </p>
        </StateCard>
      </>
    );
  }

  const toggleSort = (column: SortColumn) =>
    setSort((current) => nextSort(current, column));

  return (
    <>
      <PageHead onAdd={() => setAdding(true)} />

      {/* Status is a filter beside the others, not a tab strip: it is one
          attribute of an account among several, and the same toolbar layout
          every list screen uses says so. */}
      <div className="toolbar">
        <div className="search-field">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search name or e-mail"
            aria-label="Search name or e-mail"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <select
          className="toolbar-select"
          value={roleCode}
          aria-label="Filter by platform role"
          onChange={(event) => setRoleCode(event.target.value)}
        >
          <option value="">All roles</option>
          {roles.map((role) => (
            <option key={role.id} value={role.code}>
              {role.name}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          value={status}
          aria-label="Filter by status"
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">All statuses ({users.length})</option>
          {statuses.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label} ({option.count})
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table className="data zebra">
            <thead>
              {/* Every column sorts except the last one, which holds the
                  row actions and has nothing to order by. */}
              <tr>
                <SortableHeader column="user" label="User" sort={sort} onSort={toggleSort} />
                <SortableHeader column="email" label="E-mail" sort={sort} onSort={toggleSort} />
                <SortableHeader
                  column="role"
                  label="Platform role"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader
                  column="lastSignIn"
                  label="Last sign-in"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader column="status" label="Status" sort={sort} onSort={toggleSort} />
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty">
                      <div className="e-icon">
                        <UsersIcon aria-hidden="true" />
                      </div>
                      <h3>No accounts match</h3>
                      <p>
                        {users.length === 0
                          ? 'Nobody has signed in yet. An account appears here on a first Entra sign-in, or when you add one.'
                          : 'Clear the search, the role or the status filter to see the rest.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                visible.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isCaller={user.id === caller?.id}
                    onEditRole={() => setEditing(user)}
                    onDeactivate={() => setDeactivating(user)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <InviteUserDialog
        open={adding}
        onOpenChange={setAdding}
        roles={roles}
      />
      {editing && (
        <RoleDialog
          user={editing}
          roles={roles}
          onClose={() => setEditing(null)}
        />
      )}
      {deactivating && (
        <DeactivateDialog user={deactivating} onClose={() => setDeactivating(null)} />
      )}
    </>
  );
}

function UserRow({
  user,
  isCaller,
  onEditRole,
  onDeactivate,
}: {
  user: UserAccount;
  isCaller: boolean;
  onEditRole: () => void;
  onDeactivate: () => void;
}) {
  const update = useUpdateUser();
  const status = statusPresentation(user.status);
  const signedIn = formatSignIn(user.last_login_at);
  const deactivated = user.status === 'deactivated';

  // Disabled with a reason, because a control that silently does nothing is
  // worse than one that says why. The SERVER's 409 remains the actual control.
  const disabled = isCaller || update.isPending;
  const reason = isCaller ? OWN_ROW_REASON : undefined;

  return (
    <tr>
      <td>
        <span className="row users-identity">
          <span className={`avatar sm${deactivated ? ' muted' : ''}`} aria-hidden="true">
            {initialsOf(user.display_name)}
          </span>
          <b>{user.display_name}</b>
        </span>
      </td>
      <td className="hint">{user.email}</td>
      <td>
        {/* Exactly one badge per user, labelled from the catalogue. */}
        <span className={`badge ${badgeClassFor(user.role.code)}`}>{user.role.name}</span>
      </td>
      <td className={signedIn ? undefined : 'hint'}>{signedIn ?? 'never'}</td>
      <td>
        <span className={`badge ${status.className}`}>{status.label}</span>
      </td>
      <td>
        <div className="row-actions">
          {deactivated ? (
            <button
              type="button"
              className="btn btn-secondary btn-icon"
              title={reason ?? 'Reactivate'}
              aria-label={`Reactivate ${user.display_name}`}
              disabled={disabled}
              // Reactivation is not confirmed: it grants nothing the account
              // did not already have.
              onClick={() => update.mutate({ userId: user.id, change: { is_active: true } })}
            >
              <RotateCcw aria-hidden="true" />
            </button>
          ) : (
            <>
              {user.status === 'awaiting_role' ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  title={reason}
                  disabled={disabled}
                  onClick={onEditRole}
                >
                  Grant role
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary btn-icon"
                  title={reason ?? 'Change role'}
                  aria-label={`Change role for ${user.display_name}`}
                  disabled={disabled}
                  onClick={onEditRole}
                >
                  {/* Not a pencil. A pencil promises "edit this record's
                      fields", and nothing on this row is editable: the name and
                      the e-mail come from Entra and are never typed here. The
                      action grants a PLATFORM ROLE, so the icon is the one that
                      says "administer this person's access". */}
                  <UserCog aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-icon"
                title={reason ?? 'Deactivate'}
                aria-label={`Deactivate ${user.display_name}`}
                disabled={disabled}
                onClick={onDeactivate}
              >
                <UserX aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
